import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button } from 'react-bootstrap';
import { IoCheckmarkCircle, IoAlertCircleOutline } from 'react-icons/io5';
import { setPackagingUnitStandardsAssessments } from '../../../../store/actions/packagingUnit';
import { fetchPackagingUnitDocuments } from '../../../../store/actions/packagingUnitDocument';
import { successGlobal } from '../../../../store/reducers/notifications';
import { BASE_LANGUAGE, getMarketLanguages } from '../../../../utils/constants/marketLanguages';
import LanguageSwitch from '../languageSwitch';

const Chevron = ({ open }) => (
    <svg
        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}
    >
        <path d="M6 9l6 6 6-6" />
    </svg>
);

const RequiredBadge = () => (
    <span
        style={{
            fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
            padding: '2px 8px', borderRadius: 999,
            backgroundColor: '#0B1157', color: '#4DFFFF',
        }}
    >
        Povinné
    </span>
);

// Ponuka v poli "Priložený dokument" vychádza priamo zo súborov, ktoré si používateľ už nahral
// k tejto obalovej jednotke (záložka "Doklady od dodávateľov") - žiadny samostatný duplicitný zoznam.
const documentOptionLabel = (doc) => {
    const category = doc.checklistItemLabel || 'Nezaradené';
    const date = new Date(doc.createdAt).toLocaleDateString('sk-SK');
    return `${doc.originalName} — ${category} (${date})`;
};

// Server je zdrojom pravdy - unit.standardsAssessmentSummary.items už obsahuje pre každú položku
// jej text, aktuálne uložené applicable/note/documentId aj preklady poznámky (viď config/standardsAssessmentItems.js).
// applicable=null = položka ešte nemá žiadnu odpoveď (predvolene sa v UI ponúkne textové pole).
const buildInitialDrafts = (items) => Object.fromEntries(
    items.map(i => [i.id, {
        applicable: i.applicable === false ? false : true,
        note: i.note,
        translations: i.translations || {},
        documentId: i.documentId || '',
    }])
);

const StandardsAssessmentSection = ({ unit }) => {
    const dispatch = useDispatch();
    const summary = unit.standardsAssessmentSummary ?? { items: [], complete: false };
    const uploadedDocuments = useSelector(state => state.packagingUnitDocument.data);

    // Nevyplnená sekcia sa otvorí automaticky, aby si ju používateľ hneď všimol - keď je hotová,
    // radšej zaberá čo najmenej miesta a dá sa kedykoľvek znova rozbaliť kliknutím na hlavičku.
    const [open, setOpen] = useState(!summary.complete);
    const [drafts, setDrafts] = useState(() => buildInitialDrafts(summary.items));
    const [saving, setSaving] = useState(false);
    const [expandedInfo, setExpandedInfo] = useState({});
    // Jeden spoločný prepínač jazyka pre celú sekciu - rovnaký princíp ako pri Použitie/Popis
    // obalovej jednotky (edit.js) - prepína sa naraz text vo všetkých riadkoch, nie po jednom.
    const [textLang, setTextLang] = useState(BASE_LANGUAGE);

    const marketLanguages = useMemo(() => getMarketLanguages(unit.marketCountries), [unit.marketCountries]);
    const activeLang = marketLanguages.includes(textLang) || textLang === BASE_LANGUAGE ? textLang : BASE_LANGUAGE;

    // Rovnaký zdroj dát ako záložka "Doklady od dodávateľov" - nie samostatný zoznam, aby výber
    // dokumentu tu vždy odrážal to, čo bolo k jednotke skutočne nahraté.
    useEffect(() => {
        if (unit?._id) dispatch(fetchPackagingUnitDocuments(unit._id));
    }, [dispatch, unit?._id]);

    const toggleInfo = (itemId) => setExpandedInfo(e => ({ ...e, [itemId]: !e[itemId] }));

    const answeredCount = summary.items.filter(i => i.answered).length;
    const unansweredCount = summary.items.length - answeredCount;
    const statusMessage = summary.complete
        ? 'Sekcia je vyplnená, dokumentáciu je možné vygenerovať.'
        : `Zostáva vyplniť ${unansweredCount} z ${summary.items.length} povinných položiek (buď textom, alebo označením „Neuplatňuje sa").`;

    const savedDrafts = buildInitialDrafts(summary.items);
    const isDirty = summary.items.some((i) => {
        const draft = drafts[i.id];
        if (!draft) return false;
        const saved = savedDrafts[i.id];
        return draft.applicable !== saved.applicable
            || (draft.applicable && draft.note !== saved.note)
            || (draft.applicable && draft.documentId !== saved.documentId)
            || (draft.applicable && JSON.stringify(draft.translations) !== JSON.stringify(saved.translations));
    });

    const getTranslatedNote = (itemId) => {
        const draft = drafts[itemId] ?? savedDrafts[itemId];
        return activeLang === BASE_LANGUAGE ? draft.note : (draft.translations?.[activeLang]?.note ?? '');
    };

    const setTranslatedNote = (itemId, value) => {
        setDrafts((d) => {
            const draft = d[itemId] ?? savedDrafts[itemId];
            if (activeLang === BASE_LANGUAGE) {
                return { ...d, [itemId]: { ...draft, note: value } };
            }
            return {
                ...d,
                [itemId]: {
                    ...draft,
                    translations: { ...draft.translations, [activeLang]: { ...draft.translations?.[activeLang], note: value } },
                },
            };
        });
    };

    const updateApplicable = (itemId, applicable) => {
        setDrafts(d => ({ ...d, [itemId]: { ...(d[itemId] ?? savedDrafts[itemId]), applicable } }));
    };

    const updateDocumentId = (itemId, documentId) => {
        setDrafts(d => ({ ...d, [itemId]: { ...(d[itemId] ?? savedDrafts[itemId]), documentId } }));
    };

    const handleSaveAll = (e) => {
        e.stopPropagation();
        setSaving(true);
        const entries = summary.items.map(i => {
            const draft = drafts[i.id] ?? savedDrafts[i.id];
            return {
                itemId: i.id,
                applicable: draft.applicable,
                note: draft.applicable ? draft.note : '',
                translations: draft.applicable ? draft.translations : {},
                documentId: draft.applicable ? (draft.documentId || null) : null,
            };
        });
        dispatch(setPackagingUnitStandardsAssessments({ packagingUnitId: unit._id, entries }))
            .unwrap()
            .then(() => dispatch(successGlobal('Zoznam noriem a špecifikácií bol úspešne uložený.')))
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    return (
        <div className="mb-4" style={{ border: '1px solid #EEF0F8', borderRadius: 14, overflow: 'hidden' }}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="d-flex align-items-center gap-3 w-100"
                style={{
                    border: 'none', textAlign: 'left', cursor: 'pointer',
                    backgroundColor: summary.complete ? '#E6F4EA' : '#FBEBD8',
                    padding: '12px 16px',
                }}
            >
                {summary.complete
                    ? <IoCheckmarkCircle size={22} style={{ color: '#198754', flexShrink: 0 }} />
                    : <IoAlertCircleOutline size={22} style={{ color: '#C97D2C', flexShrink: 0 }} />}
                <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: summary.complete ? '#198754' : '#C97D2C' }}>
                            d) Zoznam noriem a špecifikácií
                        </span>
                        <RequiredBadge />
                        <span
                            style={{
                                fontSize: 10.5, fontWeight: 700, padding: '1px 8px', borderRadius: 999,
                                backgroundColor: summary.complete ? 'rgba(25,135,84,0.15)' : 'rgba(201,125,44,0.15)',
                                color: summary.complete ? '#198754' : '#C97D2C',
                            }}
                        >
                            {answeredCount}/{summary.items.length}
                        </span>
                    </div>
                    {!open && (
                        <div className="text-muted" style={{ fontSize: 11.5, marginTop: 2 }}>{statusMessage}</div>
                    )}
                </div>
                <Chevron open={open} />
            </button>

            {open && (
                <div style={{ padding: '14px 16px', background: '#fff' }}>
                    <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                        <div className="text-muted" style={{ fontSize: 12, maxWidth: 640 }}>
                            {statusMessage}
                        </div>
                        {marketLanguages.length > 0 && (
                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <span className="text-muted" style={{ fontSize: 11.5 }}>Jazyk textu:</span>
                                <LanguageSwitch languages={marketLanguages} active={activeLang} onChange={setTextLang} size="sm" />
                            </div>
                        )}
                    </div>

                    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #EEF0F8' }}>
                        {summary.items.map((item, i) => {
                            const draft = drafts[item.id] ?? savedDrafts[item.id];
                            const noteValue = getTranslatedNote(item.id);
                            const hasAttachedDocument = !!draft.documentId;
                            const rowIncomplete = draft.applicable && activeLang === BASE_LANGUAGE && !draft.note.trim() && !hasAttachedDocument;
                            return (
                                <div
                                    key={item.id}
                                    style={{
                                        padding: '12px 14px',
                                        background: i % 2 === 0 ? '#fff' : '#F9FAFF',
                                        borderTop: i > 0 ? '1px solid #EEF0F8' : 'none',
                                    }}
                                >
                                    <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-1">
                                        <div style={{ maxWidth: 560 }}>
                                            <div className="fw-semibold" style={{ fontSize: 13, color: '#0B1157' }}>
                                                {item.label}
                                            </div>
                                            {item.description && (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleInfo(item.id)}
                                                    className="d-inline-flex align-items-center gap-1 mt-1"
                                                    style={{
                                                        border: 'none', background: 'none', padding: 0,
                                                        fontSize: 11.5, fontWeight: 600, color: '#0d6efd', cursor: 'pointer',
                                                    }}
                                                >
                                                    {expandedInfo[item.id] ? 'Skryť znenie požiadavky' : 'Zobraziť znenie požiadavky'}
                                                    <Chevron open={!!expandedInfo[item.id]} />
                                                </button>
                                            )}
                                        </div>
                                        <Form.Check
                                            type="switch"
                                            id={`standards-na-${item.id}`}
                                            label="Neuplatňuje sa"
                                            checked={!draft.applicable}
                                            onChange={e => updateApplicable(item.id, !e.target.checked)}
                                        />
                                    </div>
                                    {expandedInfo[item.id] && item.description && (
                                        <div className="text-muted small mb-2" style={{ lineHeight: 1.5, maxWidth: 640, whiteSpace: 'pre-line' }}>
                                            {item.description}
                                        </div>
                                    )}
                                    {draft.applicable && (
                                        <>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                size="sm"
                                                placeholder={activeLang === BASE_LANGUAGE
                                                    ? (hasAttachedDocument
                                                        ? 'Nepovinné — dokument v prílohe stačí sám osebe, tu môžete doplniť kontext...'
                                                        : 'Uveďte konkrétnu normu, špecifikáciu alebo opis vlastného riešenia...')
                                                    : 'Preklad (nepovinné)...'}
                                                value={noteValue}
                                                onChange={e => setTranslatedNote(item.id, e.target.value)}
                                                isInvalid={rowIncomplete}
                                            />
                                            {hasAttachedDocument && (
                                                <div className="d-flex align-items-center gap-1 mt-2" style={{ fontSize: 11.5, color: '#198754', fontWeight: 600 }}>
                                                    <IoCheckmarkCircle size={14} style={{ flexShrink: 0 }} />
                                                    Podmienka splnená — dokument je priložený v prílohe, text nie je potrebné vypĺňať.
                                                </div>
                                            )}
                                            <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                                                {uploadedDocuments?.length > 0 ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>Nahratý dokument (bod f):</span>
                                                        <Form.Select
                                                            size="sm"
                                                            value={draft.documentId || ''}
                                                            onChange={e => updateDocumentId(item.id, e.target.value)}
                                                            style={{ minWidth: 220 }}
                                                        >
                                                            <option value="">— žiadny —</option>
                                                            {uploadedDocuments.map(doc => (
                                                                <option key={doc._id} value={doc._id}>{documentOptionLabel(doc)}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted" style={{ fontSize: 11.5, fontStyle: 'italic' }}>
                                                        Zatiaľ nemáte nahraté žiadne dokumenty (záložka „Doklady od dodávateľov")
                                                    </span>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="d-flex justify-content-end mt-3">
                        <Button className="btn-custom-small" disabled={!isDirty || saving} onClick={handleSaveAll}>
                            {saving ? 'Ukladá sa...' : 'Uložiť zmeny'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StandardsAssessmentSection;
